import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { safeStorage } from './safeStorage'
import { supabase } from './supabase'
import { fetchMyProfile } from './myProfile'

// Official TMDB provider logo paths
export const STREAMING = [
  { id: 8,    name: 'Netflix',    color: '#e50914', logo: '/pbpMk2JmcoNnQwx5JGpXngfoWtp.jpg',
    deepLink: q => `nflx://www.netflix.com/search?q=${encodeURIComponent(q)}`,
    search:   q => `https://www.netflix.com/search?q=${encodeURIComponent(q)}` },
  { id: 9,    name: 'Prime',      color: '#00a8e0', logo: '/pvske1MyAoymrs5bguRfVqYiM9a.jpg',
    deepLink: q => `aiv://aiv/search?phrase=${encodeURIComponent(q)}`,
    search:   q => `https://www.amazon.com/s?k=${encodeURIComponent(q)}&i=instant-video` },
  { id: 15,   name: 'Hulu',       color: '#1ce783', logo: '/bxBlRPEPpMVDc4jMhSrTf2339DW.jpg',
    deepLink: q => `hulu://search?query=${encodeURIComponent(q)}`,
    search:   q => `https://www.hulu.com/search?q=${encodeURIComponent(q)}` },
  { id: 337,  name: 'Disney+',    color: '#0063e5', logo: '/97yvRBw1GzX7fXprcF80er19ot.jpg',
    deepLink: q => `disneyplus://search/${encodeURIComponent(q)}`,
    search:   q => `https://www.disneyplus.com/search?q=${encodeURIComponent(q)}` },
  { id: 350,  name: 'Apple TV+',  color: '#555555', logo: '/mcbz1LgtErU9p4UdbZ0rG6RTWHX.jpg',
    deepLink: q => `videos://search?q=${encodeURIComponent(q)}`,
    search:   q => `https://tv.apple.com/search?term=${encodeURIComponent(q)}` },
  { id: 1899, name: 'Max',        color: '#002be7', logo: '/jbe4gVSfRlbPTdESXhEKpornsfu.jpg',
    deepLink: q => `max://search?q=${encodeURIComponent(q)}`,
    search:   q => `https://www.max.com/search?q=${encodeURIComponent(q)}` },
  { id: 386,  name: 'Peacock',    color: '#f5a623', logo: '/2aGrp1xw3qhwCYvNGAJZPdjfeeX.jpg',
    deepLink: q => `peacocktv://search?q=${encodeURIComponent(q)}`,
    search:   q => `https://www.peacocktv.com/search?q=${encodeURIComponent(q)}` },
  // TMDB retired the plain "Paramount+" provider (531) and split it into tiers;
  // 2303 (Premium) is the flagship plan and has the higher US display priority of
  // the two, so it's the canonical id here (Jason 2026-08-02, PopularOnServices
  // silently returning 0 results for every Paramount+ subscriber). Essential
  // (2616) exists too — a show tagged only under it won't match here, same
  // fragmented-catalog gap other split services have; full multi-id matching is
  // scoped into the services-insight rebuild, not patched piecemeal here.
  { id: 2303, name: 'Paramount+', color: '#0064ff', logo: null,
    search:   q => `https://www.paramountplus.com/search/?query=${encodeURIComponent(q)}` },
  // Cable-network & free/live services — the ones cable-channel shows (Discovery,
  // AMC, etc.) actually stream on. IDs + logos are TMDB US watch-provider values,
  // pulled from /watch/providers/tv and verified (this caught wrong ids I'd guessed:
  // Philo was 1854=AMC-channel, Sling was 299, Showtime 37 no longer a US provider).
  { id: 520,  name: 'Discovery+', color: '#1a6dff', logo: '/eMTnWwNVtThkjvQA6zwxaoJG9NE.jpg',
    search:   q => `https://www.discoveryplus.com/search?q=${encodeURIComponent(q)}` },
  { id: 43,   name: 'Starz',      color: '#9333ea', logo: '/yIKwylTLP1u8gl84Is7FItpYLGL.jpg',
    search:   q => `https://www.starz.com/us/en/search?q=${encodeURIComponent(q)}` },
  { id: 526,  name: 'AMC+',       color: '#0aa4d6', logo: '/ovmu6uot1XVvsemM2dDySXLiX57.jpg',
    search:   q => `https://www.amcplus.com/search?q=${encodeURIComponent(q)}` },
  { id: 73,   name: 'Tubi',       color: '#ff5a1f', logo: '/zLYr7OPvpskMA4S79E3vlCi71iC.jpg',
    search:   q => `https://tubitv.com/search/${encodeURIComponent(q)}` },
  { id: 300,  name: 'Pluto TV',   color: '#3949ab', logo: '/dB8G41Q6tSL5NBisrIeqByfepBc.jpg',
    search:   q => `https://pluto.tv/en/search?query=${encodeURIComponent(q)}` },
  { id: 207,  name: 'Roku',       color: '#6f1ab1', logo: '/wQzSN83BnWVgO7xEh0SeTVqtrFv.jpg',
    search:   q => `https://therokuchannel.roku.com/search/${encodeURIComponent(q)}` },
  { id: 2383, name: 'Philo',      color: '#ec5a3c', logo: '/ptmbGSttkyzawLbxx9MElmxKuVo.jpg',
    search:   q => `https://www.philo.com/search?q=${encodeURIComponent(q)}` },
  { id: 1809, name: 'Sling TV',   color: '#0072ce', logo: '/7i7tcQoZH7iM9fs28OzGCNMdt7W.jpg',
    search:   q => `https://watch.sling.com/search/${encodeURIComponent(q)}` },
  { id: 257,  name: 'fuboTV',     color: '#e8730b', logo: '/9BgaNQRMDvVlji1JBZi6tcfxpKx.jpg',
    search:   q => `https://www.fubo.tv/search?q=${encodeURIComponent(q)}` },
  { id: 151,  name: 'BritBox',    color: '#1b3a6b', logo: '/8oA7IcDNNUtBa9JYB5kQ8hrDz5o.jpg',
    search:   q => `https://www.britbox.com/us/search/${encodeURIComponent(q)}` },
  { id: 283,  name: 'Crunchyroll',color: '#f47521', logo: '/fzN5Jok5Ig1eJ7gyNGoMhnLSCfh.jpg',
    search:   q => `https://www.crunchyroll.com/search?q=${encodeURIComponent(q)}` },
  { id: 192,  name: 'YouTube',    color: '#ff0000', logo: '/pTnn5JwWr4p3pG8H6VrpiQo7Vs0.jpg',
    search:   q => `https://www.youtube.com/results?search_query=${encodeURIComponent(q)}` },
  { id: 3,    name: 'Google Play',color: '#4285f4', logo: '/8z7rC8uIDaTM91X0ZfkRf04ydj2.jpg',
    search:   q => `https://play.google.com/store/search?q=${encodeURIComponent(q)}&c=movies` },
  { id: 2,    name: 'Apple iTunes',color: '#fc3c44', logo: '/SPnB1qiCkYfirS2it3hZORwGVn.jpg',
    search:   q => `https://tv.apple.com/search?term=${encodeURIComponent(q)}` },
]

// Approximate US monthly list prices (standard/base plan, mid-2026), for the
// "Roll Credits on Services" savings estimate ONLY — not shown anywhere else.
// No live pricing API exists for this (TMDB doesn't carry it); these are a
// manually-maintained snapshot and WILL drift as services reprice. Free/ad-
// supported services (Tubi, Pluto, Roku) and storefronts (YouTube, Google
// Play, Apple iTunes — pay-per-title, not a subscription) are deliberately
// omitted: there's nothing to "cancel."
export const MONTHLY_PRICE = {
  8: 17.99,    // Netflix Standard
  9: 8.99,     // Prime Video (standalone)
  15: 9.99,    // Hulu (with ads)
  337: 9.99,   // Disney+ (with ads)
  350: 9.99,   // Apple TV+
  1899: 9.99,  // Max (with ads)
  386: 7.99,   // Peacock
  2303: 12.99, // Paramount+ Premium
  520: 4.99,   // Discovery+
  43: 9.99,    // Starz
  526: 8.99,   // AMC+
  2383: 28,    // Philo
  1809: 45.99, // Sling TV
  257: 79.99,  // fuboTV
  151: 8.99,   // BritBox
  283: 7.99,   // Crunchyroll
}

const LOGO_BASE = 'https://image.tmdb.org/t/p/w45'

const STORAGE_KEY = 'nextep_services'

// TMDB provider ids that have since been retired/renamed, mapped to their
// replacement. Found live 2026-09-17: a QA account's persisted selection still
// carried the old Paramount+ id (531, retired per the STREAMING comment above
// when TMDB split it into tiers, 2026-08-02) — nothing had ever migrated an
// ALREADY-SAVED selection, so it lingered forever, invisible (no tile renders
// for a retired id) but still counted in `selected.size`, permanently
// desyncing the Settings hub's "N selected" from the streaming page's own
// visible count of checked tiles. Same shape as the key-rename migration just
// below; add future retirements here rather than patching call sites.
const LEGACY_ID_MAP = { 531: 2303 }
const KNOWN_IDS = new Set(STREAMING.map(s => s.id))
function normalizeIds(ids) {
  const mapped = (Array.isArray(ids) ? ids : []).map(id => LEGACY_ID_MAP[id] ?? id)
  return [...new Set(mapped)].filter(id => KNOWN_IDS.has(id))
}

export function useMyServices(user) {
  const uid = user?.id || null
  const [selected, setSelected] = useState(() => {
    try {
      let raw = safeStorage.get(STORAGE_KEY)
      // One-time migration: the services key was renamed 'nextep_my_services' →
      // 'nextep_services' at some point WITHOUT migrating, so existing users' picks
      // silently vanished from launch buttons + "on your services" rows. Recover them
      // (Jason 2026-06-30 — his [8,9,337] selection was stranded in the old key).
      if (!raw || raw === '[]') {
        const legacy = safeStorage.get('nextep_my_services')
        if (legacy && legacy !== '[]') { raw = legacy; safeStorage.set(STORAGE_KEY, legacy) }
      }
      const ids = normalizeIds(JSON.parse(raw || '[]'))
      safeStorage.set(STORAGE_KEY, JSON.stringify(ids))
      return new Set(ids)
    } catch { return new Set() }
  })
  const hydratedFor = useRef(null)

  // Persist to the profile (durable, cross-device). localStorage is per-ORIGIN, so
  // the app.nextep.show domain move stranded everyone's picks → the "Popular on your
  // services" row silently vanished. The profile is now the source of truth. Read-
  // modify-write so we don't clobber other ui_prefs keys (e.g. useUiPrefs layout).
  const persistServer = useCallback((ids) => {
    if (!uid) return
    supabase.rpc('get_my_profile').maybeSingle()   // own ui_prefs via definer RPC (column-scoped out for authenticated)
      .then(({ data }) => {
        const merged = { ...(data?.ui_prefs || {}), streaming_services: ids }
        supabase.from('profiles').update({ ui_prefs: merged }).eq('id', uid).then(() => {}, () => {})
      }, () => {})
  }, [uid])

  // Hydrate once the user is known: this device's picks win if it has any (and get
  // backed up to the profile so they're durable); otherwise recover from the profile
  // — which is what heals a domain/device change where localStorage came up empty.
  useEffect(() => {
    if (!uid || hydratedFor.current === uid) return
    hydratedFor.current = uid
    fetchMyProfile(uid)   // own ui_prefs via definer RPC (column-scoped out for authenticated)
      .then(({ data }) => {
        const server = normalizeIds(data?.ui_prefs?.streaming_services)
        setSelected(prev => {
          if (prev.size > 0) { persistServer([...prev]); return prev }
          if (server.length) {
            safeStorage.set(STORAGE_KEY, JSON.stringify(server))
            return new Set(server)
          }
          return prev
        })
      }, () => {})
  }, [uid, persistServer])

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      const ids = [...next]
      safeStorage.set(STORAGE_KEY, JSON.stringify(ids))
      persistServer(ids)
      // Every other mounted instance (the Home deck's Watch-on pill while the
      // "Where it's playing" sheet adds a service, 17.165) re-reads storage.
      try { window.dispatchEvent(new CustomEvent('nextep:services-changed', { detail: { ids } })) } catch {}
      return next
    })
  }
  useEffect(() => {
    const on = (e) => { const ids = normalizeIds(e.detail?.ids); setSelected(prev => (prev.size === ids.length && ids.every(i => prev.has(i))) ? prev : new Set(ids)) }
    window.addEventListener('nextep:services-changed', on)
    return () => window.removeEventListener('nextep:services-changed', on)
  }, [])

  const has = (id) => selected.has(id)
  // Memoize so consumers (StreamingLaunchButton useEffect deps) don't see a new
  // array reference on every render — was causing infinite re-render loops.
  const list = useMemo(() => STREAMING.filter(s => selected.has(s.id)), [selected])

  return { selected, list, toggle, has }
}

// Service logo image — uses official TMDB logo, falls back to colored badge
export function ServiceLogo({ service, size = 24, style: extraStyle = {} }) {
  const s = typeof service === 'number' ? STREAMING.find(x => x.id === service) : service
  if (!s) return null
  if (s.logo) {
    return (
      <img
        src={`${LOGO_BASE}${s.logo}`}
        alt={s.name}
        title={s.name}
        style={{
          width: size, height: size, borderRadius: size * 0.2,
          objectFit: 'cover', flexShrink: 0,
          ...extraStyle,
        }}
      />
    )
  }
  // Fallback colored badge
  return (
    <div title={s.name} style={{
      width: size, height: size, borderRadius: size * 0.2,
      background: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.32, fontWeight: 800, color: '#fff', flexShrink: 0,
      letterSpacing: '-0.3px', fontFamily: 'var(--font)',
      ...extraStyle,
    }}>
      {s.name.slice(0, 2).toUpperCase()}
    </div>
  )
}


// Service picker grid for Settings (3-col, with official logos)
export function ServicePicker({ myServices }) {
  if (!myServices) return (
    <div style={{ color:'var(--text-muted)', fontSize:12, padding:'12px 0', textAlign:'center' }}>
      Service picker unavailable in preview mode
    </div>
  )
  return (
    <div>
      <p style={{ color: 'var(--amber)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 10px' }}>
        My Streaming Services
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
        {STREAMING.map(s => {
          const on = myServices.has(s.id)
          return (
            <button key={s.id} onClick={() => myServices.toggle(s.id)} style={{
              background: on ? `${s.color}18` : 'color-mix(in srgb, var(--text) 4%, transparent)',
              border: `1px solid ${on ? s.color : 'var(--border)'}`,
              borderRadius:'var(--radius-sm)', padding: '10px 8px', cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
              boxShadow: on ? `0 0 12px ${s.color}33` : 'none',
              transition: 'all 0.15s', fontFamily: 'var(--font)',
              position: 'relative',
            }}>
              <ServiceLogo service={s} size={32} />
              <span style={{ color: on ? 'var(--text)' : 'var(--text-muted)', fontSize: 11, fontWeight: on ? 700 : 500, textAlign: 'center' }}>
                {s.name}
              </span>
              {on && (
                <div style={{
                  position: 'absolute', top: 5, right: 5,
                  width: 14, height: 14, borderRadius:'var(--radius-full)',
                  background: 'var(--amber)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <span style={{ color: 'var(--amber-ink)', fontSize: 8, fontWeight: 800 }}>✓</span>
                </div>
              )}
            </button>
          )
        })}
      </div>
      <p style={{ color: 'var(--text-dim)', fontSize: 11, margin: '8px 0 0' }}>
        {myServices.list.length} service{myServices.list.length !== 1 ? 's' : ''} selected
      </p>
    </div>
  )
}
